import React, { memo, ReactNode, useCallback, useMemo } from "react";
import { Link, Mark, Box } from "@chakra-ui/react";
import reactStringReplace from "react-string-replace";

import DistributionGroupMention from "modules/_shared/mentions/distribution-group-mention";
import TagMention from "modules/_shared/mentions/tag-mention";
import UserMention from "modules/_shared/mentions/user-mention";
import colors from "app/colors";
import {
  COMMON_DATE_CALENDAR_FORMATS,
  DATE_MARKDOWN,
  DISTRIBUTION_GROUP_MARKDOWN,
  TAG_MARKDOWN,
  USER_MARKDOWN,
} from "modules/utils/Constants";
import highlightWordsRegex from "./_shared/highlight-words-regex";
import Linkify from "linkify-react";
import type { IntermediateRepresentation } from "linkifyjs";
import usePlainText from "modules/hooks/use-plain-text";
import moment from "moment";

interface IProps {
  text?: string | null;
  highlightText?: string[] | null;
  linkify?: boolean;
  suffix?: ReactNode;
  highlight?: boolean;
}

const TAG_REGEX = new RegExp(`\\{\\{${TAG_MARKDOWN}=([0-9a-fA-F]{24})\\}\\}`);
const USER_REGEX = new RegExp(`\\{\\{${USER_MARKDOWN}=([0-9a-fA-F]{24})\\}\\}`);
const DISTRIBUTION_GROUP_REGEX = new RegExp(
  `\\{\\{${DISTRIBUTION_GROUP_MARKDOWN}=([0-9a-fA-F]{24})\\}\\}`,
);

const DATE_REGEX = new RegExp(
  `\\{\\{${DATE_MARKDOWN}=([0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(?:\\.[0-9]{3})?Z)\\}\\}`,
);

const MessagePlainText = ({
  text,
  highlightText,
  linkify = true,
  suffix,
  highlight = true,
}: IProps) => {
  //https://github.com/iansinnott/react-string-replace/issues/80
  const { plainText } = usePlainText(text);

  const highlightRegex = useMemo(
    () =>
      highlight && highlightText
        ? highlightWordsRegex(highlightText)
        : undefined,
    [highlight, highlightText],
  );

  const truncatedText = useMemo(() => {
    if (!text || !highlightRegex) {
      return text;
    }

    if (!plainText) {
      return plainText;
    }

    // Don't match on any tags or callouts. E.g. if the user searches 633 don't match on "{{uid=633c4ecc17962227154e1926}}"
    const dumbText = plainText.replaceAll(/\{\{.+\}\}/g, "");
    const matches = dumbText
      .split(highlightRegex)
      .filter((match) => highlightRegex.test(match));

    if (!matches.length) {
      return plainText;
    }

    const firstMatch = matches[0];
    const index = plainText.indexOf(firstMatch);

    // TODO: Fix the edge case where a tag or callout could be cut off in the middle and thus won't be converted into a smart mention
    // E.g.: "…13ba734}} @joshjonte hey guys, if we changed the text message to include a colon I think iOS…"
    const leadingCharacterCountBuffer = 45;
    if (index < leadingCharacterCountBuffer) {
      return plainText;
    }

    return `…${plainText.slice(index - leadingCharacterCountBuffer)}`;
  }, [highlightRegex, text, plainText]);

  const renderedMessageNodeArray = useMemo(() => {
    if (!truncatedText) return;

    const replacements = [
      {
        regex: DISTRIBUTION_GROUP_REGEX,
        render: (id: string, i: number) => (
          <DistributionGroupMention
            key={`${DISTRIBUTION_GROUP_MARKDOWN}_${id}_${i}`}
            id={id}
            highlight={highlight}
          />
        ),
      },
      {
        regex: TAG_REGEX,
        render: (id: string, i: number) => (
          <TagMention
            key={`${TAG_MARKDOWN}_${id}_${i}`}
            id={id}
            highlight={highlight}
          />
        ),
      },
      {
        regex: USER_REGEX,
        render: (id: string, i: number) => (
          <UserMention
            key={`${USER_MARKDOWN}_${id}_${i}`}
            id={id}
            highlight={highlight}
          />
        ),
      },
      {
        regex: DATE_REGEX,
        render: (date: string, i: number) => (
          <Box key={`${DATE_MARKDOWN}_${date}_${i}`} as="span">
            {moment(date).calendar(COMMON_DATE_CALENDAR_FORMATS)}
          </Box>
        ),
      },
      {
        regex: highlightRegex,
        render: (match: string, i: number) => (
          <Mark
            key={`highlight-${i}`}
            sx={{ bg: colors.yellow, margin: "0 !important" }}
          >
            {match}
          </Mark>
        ),
      },
    ];

    return replacements.reduce<string | ReactNode[]>(
      (result, { regex, render }) => reactStringReplace(result, regex, render),
      truncatedText,
    );
  }, [highlight, highlightRegex, truncatedText]);

  const renderLink = useCallback(
    ({ attributes, content }: IntermediateRepresentation) => (
      <Link
        target="_blank"
        href={attributes.href}
        color={colors.blue}
        rel="noreferrer"
        display="inline-block"
        overflowWrap="anywhere"
      >
        {content}
      </Link>
    ),
    [],
  );

  const linkifyOptions = useMemo(() => ({ render: renderLink }), [renderLink]);

  if (text == null) {
    return null;
  }

  if (linkify) {
    return (
      <Linkify options={linkifyOptions}>
        <Box overflowWrap="anywhere" as="span" whiteSpace="pre-line">
          {renderedMessageNodeArray}
          {suffix}
        </Box>
      </Linkify>
    );
  }

  return (
    <Box overflowWrap="anywhere" as="span">
      {renderedMessageNodeArray}
      {suffix}
    </Box>
  );
};

export default memo(MessagePlainText);
